/**
 * Speech Analysis API
 *
 * API functions for audio analysis and sales coaching feedback.
 * Only available to users with isOwner permission.
 */

export interface LLMScoreItem {
    score: number;
    feedback: string;
    present?: boolean;
}

export interface LLMAnalysis {
    upsell_quality: LLMScoreItem;
    politeness: LLMScoreItem;
    kindness: LLMScoreItem;
    customer_benefit: LLMScoreItem;
    opening_question: LLMScoreItem & { present: boolean };
    soft_sell: LLMScoreItem;
    overall_score: number;
    summary: string;
    top_strengths: string[];
    improvements: string[];
    error?: string;
}

export interface TranscriptSegment {
    start: number;
    end: number;
    text: string;
}

export interface ProsodyLabel {
    id: string;
    severity: "info" | "warn" | "alert";
    message: string;
}

export interface ProsodyHighlight {
    t: number;
    type: string;
    message: string;
}

export interface ProsodyMetrics {
    wpm: number;
    speech_rate_label: string;
    silence_ratio: number;
    pause_count: number;
    pauses_per_min: number;
    avg_pause_ms: number;
    long_pause_count: number;
    filler_words?: {
        total: number;
        per_min: number;
        by_type: Record<string, number>;
    };
    prosody?: {
        pitch_mean_hz: number;
        pitch_range_hz: number;
        pitch_variability: "LOW" | "MEDIUM" | "HIGH";
        energy_mean: number;
        energy_variability: "LOW" | "MEDIUM" | "HIGH";
        voicing_ratio: number;
        monotony_score: number;
    };
}

export interface ProsodyAnalysis {
    available: boolean;
    error?: string;
    meta?: {
        profile: string;
        language: string;
        duration_sec: number;
        processed_at: string;
        privacy: {
            stored: boolean;
            third_party: boolean;
        };
    };
    transcript?: {
        text: string;
        segments: TranscriptSegment[];
    };
    metrics?: ProsodyMetrics;
    keywords?: {
        required: Array<{ keyword: string; present: boolean; hits: number }>;
        forbidden: Array<{ keyword: string; present: boolean; hits: number }>;
    };
    labels?: ProsodyLabel[];
    tips?: string[];
    highlights?: ProsodyHighlight[];
    overall_score?: number;
    scores_breakdown?: {
        pace: number;
        pauses: number;
        fillers: number;
        prosody: number;
        structure: number;
    };
    // Truly raw data from Python service (no interpretation)
    _raw?: {
        meta: {
            profile: string;
            language: string;
            duration_sec: number;
            processed_at: string;
            privacy: {
                stored: boolean;
                third_party: boolean;
            };
        };
        vad: {
            total_speech_sec: number;
            total_silence_sec: number;
            total_duration_sec: number;
            silence_ratio: number;
            pause_count: number;
            long_pause_count: number;
            avg_pause_ms: number;
            pauses_per_min: number;
            speech_segments_count: number;
        };
        prosody: {
            pitch_mean_hz: number;
            pitch_std_hz: number;
            pitch_min_hz: number;
            pitch_max_hz: number;
            pitch_range_hz: number;
            energy_mean: number;
            energy_std: number;
            energy_variability: number;
            voicing_ratio: number;
            jitter_local: number | null;
            shimmer_local: number | null;
        };
        text: {
            word_count: number;
            wpm: number;
        };
    };
}

export interface SpeechAnalysisResult {
    success: boolean;
    meta: {
        profile: string;
        language: string;
        duration_sec: number;
        processed_at: string;
        prosody_enabled: boolean;
    };
    transcript: {
        text: string;
        segments: TranscriptSegment[];
    };
    llm_analysis: LLMAnalysis;
    prosody_analysis?: ProsodyAnalysis;
    error?: string;
    message?: string;
}

export interface AnalyzeAudioParams {
    token: string;
    audioData: string; // base64 encoded audio
    audioFormat?: string;
    language?: string;
    enableProsodyAnalysis?: boolean;
}

/**
 * Analyze audio recording for sales coaching feedback
 */
export const analyzeAudio = async ({
    token,
    audioData,
    audioFormat = "webm",
    language = "fr",
    enableProsodyAnalysis = false,
}: AnalyzeAudioParams): Promise<SpeechAnalysisResult> => {
    const baseUrl = process.env.REACT_APP_API_BASE_URL;
    if (!baseUrl) {
        throw new Error("REACT_APP_API_BASE_URL environment variable is not defined");
    }

    const response = await fetch(`${baseUrl}/analyze-audio`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
            audioData,
            audioFormat,
            language,
            enableProsodyAnalysis,
        }),
    });

    const data = await response.json();

    if (!response.ok) {
        throw new Error(data.message || `Analysis failed with status ${response.status}`);
    }

    return data;
};

/**
 * Check health of the speech analyzer service
 */
export const checkSpeechAnalyzerHealth = async (token: string): Promise<{ status: string; message?: string }> => {
    const baseUrl = process.env.REACT_APP_API_BASE_URL;
    if (!baseUrl) {
        throw new Error("REACT_APP_API_BASE_URL environment variable is not defined");
    }

    const response = await fetch(`${baseUrl}/speech-analyzer-health`, {
        method: "GET",
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });

    return response.json();
};

/**
 * Get available analysis profiles
 */
export const getSpeechAnalysisProfiles = async (token: string): Promise<{ profiles: Record<string, any> }> => {
    const baseUrl = process.env.REACT_APP_API_BASE_URL;
    if (!baseUrl) {
        throw new Error("REACT_APP_API_BASE_URL environment variable is not defined");
    }

    const response = await fetch(`${baseUrl}/speech-analysis-profiles`, {
        method: "GET",
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });

    return response.json();
};
